// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { GridContainerProps } from "@sc/MobiusOverrides/GridContainer/GridContainer";
import { mergeWithCss } from "@sc/XDL/Utilities/mergeWithCss";
import React, { Children, FC, PropsWithChildren, useState } from "react";

export interface ListLineContainerProps {
    extendedStyles?: ListLineContainerStyles;
    [key: string]: any;
}
interface OwnProps {
    Image: FC<PropsWithChildren>;
    Description: FC<PropsWithChildren>;
    Price: FC<PropsWithChildren>;
    Actions: FC<PropsWithChildren>;
    Notes: FC<PropsWithChildren>;
    DeleteItem: FC<PropsWithChildren>;
    Notification: FC<PropsWithChildren>;
}
export interface ListLineContainerStyles {
    container?: InjectableCss;
    contentsWrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    image?: InjectableCss;
    descriptionWrapper?: GridItemProps;
    description?: InjectableCss;
    price?: InjectableCss;
    notification?: InjectableCss;
    actionsWrapper?: GridItemProps;
    actions?: InjectableCss;
    notes?: InjectableCss;
    deleteItem?: InjectableCss;
}

export const listLineContainerStyles: ListLineContainerStyles = {};

const names = {
    Image: "Image",
    Description: "Description",
    Price: "Price",
    Actions: "Actions",
    DeleteItem: "DeleteItem",
    Notes: "Notes",
    Notification: "Notification",
};

const Notification: FC = () => null;
const Image: FC = () => null;
const Description: FC = () => null;
const Price: FC = () => null;
const DeleteItem: FC = () => null;
const Notes: FC = () => null;
const Actions: FC = () => null;

const ListLineContainer: React.FC<PropsWithChildren<ListLineContainerProps>> & OwnProps = ({
    children,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeWithCss(listLineContainerStyles, extendedStyles));

    const { Image, Description, Price, Actions, DeleteItem, Notification, Notes } = React.useMemo(() => {
        const childrenArray = Children.toArray(children) as React.ReactElement<PropsWithChildren>[];
        const MappedChilds = childrenArray.reduce(
            (results, current) => {
                const currentName = (current.type as React.NamedExoticComponent<any>)?.displayName;
                if (currentName) {
                    results[currentName] = current;
                }
                return results;
            },
            {} as Record<string, React.ReactElement<PropsWithChildren> | null>,
        );
        const slots = Object.entries(names).reduce(
            (results, [key, value], idx) => {
                results[value] = MappedChilds[value] || null;
                return results;
            },
            {} as Record<string, React.ReactElement<PropsWithChildren> | null>,
        );

        return slots;
    }, [children]);

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.contentsWrapper}>
                <StyledWrapper {...styles.image}>{Image?.props?.children}</StyledWrapper>
                <GridContainer {...styles.gridContainer}>
                    <GridItem {...styles.descriptionWrapper}>
                        <StyledWrapper {...styles.description}>{Description?.props?.children}</StyledWrapper>
                        <StyledWrapper {...styles.price}>{Price?.props?.children}</StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.actionsWrapper}>
                        <StyledWrapper {...styles.actions}>{Actions?.props?.children}</StyledWrapper>
                        <StyledWrapper {...styles.deleteItem}>{DeleteItem?.props?.children}</StyledWrapper>
                    </GridItem>
                    <StyledWrapper {...styles.notification}>{Notification?.props?.children}</StyledWrapper>
                </GridContainer>
            </StyledWrapper>
            <StyledWrapper {...styles.notes}>{Notes?.props?.children}</StyledWrapper>
        </StyledWrapper>
    );
};

ListLineContainer.Notification = Notification;
ListLineContainer.Image = Image;
ListLineContainer.Description = Description;
ListLineContainer.Price = Price;
ListLineContainer.DeleteItem = DeleteItem;
ListLineContainer.Notes = Notes;
ListLineContainer.Actions = Actions;

ListLineContainer.Notification.displayName = names.Notification;
ListLineContainer.Image.displayName = names.Image;
ListLineContainer.Description.displayName = names.Description;
ListLineContainer.Price.displayName = names.Price;
ListLineContainer.DeleteItem.displayName = names.DeleteItem;
ListLineContainer.Notes.displayName = names.Notes;
ListLineContainer.Actions.displayName = names.Actions;

export default ListLineContainer;
